import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { styled } from '@mui/material';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { Area, PolicyType, User } from '../../types';
import { DasloopAlert } from '../../types/Alert';
import {
  WebSocketAlert,
  WebSocketProjectAttendanceReportCreated,
} from '../../types/Websocket';

import { getAreasOfProject } from '../../apis/AreaApi';
import { getFloorPlans } from '../../apis/FloorPlanApi';
import {
  getMyProjectPolicies,
  getProjectContact,
  getProjectFeatureList,
  getProjectRole,
  getProjectUsers,
} from '../../apis/ProjectApi';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useAlertQueueTimeOut } from '../../hooks/useAlertQueueTimeOut';
import useAlertSnackBar from '../../hooks/useAlertSnackBar';
import useUnlockRequest from '../../hooks/useUnlockRequest';
import { alertHandler } from '../../hooks/useWebSocket';
import {
  setCurrenProject,
  setCurrentMembers,
  setIsLoadingProjectContact,
  setProjectArea,
  setProjectContact,
  setProjectFloorPlan,
  setProjectPolicies,
  setProjectRole,
} from '../../slices/projectSlice';
import {
  setFeatureListMap,
  setSelectedProjectId,
} from '../../slices/systemSlice';
import { setCurrentProjectId } from '../../slices/websocketSlice';

import alertDashboard from '../../assets/images/svg/alert-dashboard.svg';
import areaManagementSvg from '../../assets/images/svg/area-management.svg';
import logSvg from '../../assets/images/svg/book-open.svg';
import downloadSvg from '../../assets/images/svg/download.svg';
import gearSvg from '../../assets/images/svg/gear.svg';
import overviewSvg from '../../assets/images/svg/img_overview.svg';
import paSvg from '../../assets/images/svg/public-announcement.svg';
import reportSvg from '../../assets/images/svg/report_svg_icon.svg';
import CloseSvgIcon from '../../assets/SvgIcon/CloseSvgIcon';
import HomeSvgIcon from '../../assets/SvgIcon/HomeSvgIcon';
import { AlertType } from '../../utils/getAlertIcon';
import { PubSubContext } from '../../utils/PubSub';
import IconContainer from '../IconContainer';
import DarkTooltip from '../Toolip/DarkTooltip';

import BasicMenu from './BasicMenu';
import SSSSMenu from './SSSSMenu';
interface SidebarProps {
  expand: boolean | undefined;
}

const Sidebar = styled('nav')<SidebarProps>`
  @keyframes expand {
    from {
      width: 60px;
    }
    to {
      width: 300px;
    }
  }

  @keyframes collapse {
    from {
      width: 300px;
    }
    to {
      width: 60px;
    }
  }

  @keyframes init {
    from {
      width: 0px;
    }
    to {
      width: 60px;
    }
  }

  position: absolute;
  z-index: 101;
  flex: 0 0 auto;
  display: flex;
  flex-flow: column;
  height: 100vh;
  color: white;
  background-color: ${({ theme }) => theme.color.primary.$100};
  animation: ${({ expand }) =>
    expand
      ? 'expand 0.5s forwards'
      : expand === undefined
        ? 'init 0.5s forwards'
        : 'collapse 0.5s forwards'};
  & > .backward-button {
    flex: 0 0 auto;
    height: 64px;
    cursor: pointer;
    background-color: ${({ theme }) => theme.externalColor.secondary.$140};
    & > div {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  & > .feature-list {
    flex: 100;
    padding: 0;
    margin: 0;
    overflow-x: auto;
    &::-webkit-scrollbar {
      display: none;
    }

    & .event-block {
      height: 2px;
      // Used for debugging
      /* background-color: red; */
    }
  }

  & > .bottom-list {
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 0;
    margin: 0;
  }

  & a {
    cursor: pointer;
    display: flex;
    text-decoration: none;
    list-style-type: none;
    color: white;
    gap: 0.5rem;
    padding-left: ${({ expand }) => (expand ? '8px' : '')};
    & > div {
      width: ${({ expand }) => (expand ? '10%' : 'auto')};
    }

    &.active {
      background-color: ${({ theme }) => theme.color.secondary.$100};
    }

    & .icon {
      width: 100%;
      height: 40px;
    }

    &.home {
      display: flex;
      height: 50px;
    }

    & > div {
      display: flex;
      justify-content: center;
      align-items: center;
      width: ${({ expand }) => (expand ? '10%' : '100%')};

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
    & > span {
      display: flex;
      align-items: center;
      width: ${({ expand }) => (expand ? '90%' : 'auto')};
    }
  }
`;

const ENTER_DELAY = 1000;

export const Divider = styled('div', {
  label: 'ProjectSidebar-Divider',
})<SidebarProps>`
  margin: 10px;
  margin-right: ${({ expand }) => (expand ? '32px' : '20px')};
  margin-left: ${({ expand }) => (expand ? '16px' : '12px')};

  &:before {
    display: block;
    content: '';
    width: 100%;
    height: 2px;
    background-color: rgba(255, 255, 255, 0.5);
    border-radius: 1px;
  }
`;

const Icon = styled('div')`
  & > svg {
    display: block;
    width: 40px;
    height: 40px;
  }
`;

const ExpandButton = styled('div')`
  display: flex;
  padding: 0px;
  flex-direction: row;
  & > svg {
    display: flex;
    width: auto;
    cursor: pointer;
  }
`;

const ProjectSidebar = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const pubSub = useContext(PubSubContext);
  const { projectId } = useParams();
  const dispatch = useAppDispatch();
  const project = useAppSelector((store) => store.projects.currentProject);
  const selectedProjectId = useAppSelector(
    (store) => store.system.selectedProjectId,
  );
  const currentProjectSetting = useAppSelector(
    (store) => store.projects.currentProjectSetting,
  );

  const projectRole = useAppSelector((store) => store.projects.role);
  const featureListRef = useRef<HTMLUListElement | null>(null);
  const commonFeatureListRef = useRef<HTMLUListElement | null>(null);
  const topIndicatorRef = useRef<HTMLDivElement | null>(null);
  const bottomIndicatorRef = useRef<HTMLDivElement | null>(null);
  const commonFeatureTopIndicatorRef = useRef<HTMLDivElement | null>(null);
  const commonFeatureBottomIndicatorRef = useRef<HTMLDivElement | null>(null);
  const [websocketAlert, setWebsocketAlert] = useState<WebSocketAlert | null>(
    null,
  );
  const [featureScroll, setFeatureScroll] = useState({
    showTop: false,
    showBottom: true,
  });
  const [commonFeatureScroll, setCommonFeatureScroll] = useState({
    showTop: false,
    showBottom: true,
  });
  const [expand, setExpand] = useState<boolean | undefined>(undefined);

  useAlertSnackBar(selectedProjectId ?? '', websocketAlert);

  useAlertQueueTimeOut(projectId, 30000);

  useUnlockRequest({ project });

  useEffect(() => {
    let featureObserver: IntersectionObserver | undefined;
    let commonFeatureObserver: IntersectionObserver | undefined;
    if (featureListRef.current) {
      featureObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.target.id === 'top-indicator') {
              if (entry.isIntersecting) {
                setFeatureScroll((origin) => ({
                  ...origin,
                  showTop: false,
                }));
              } else {
                setFeatureScroll((origin) => ({
                  ...origin,
                  showTop: true,
                }));
              }
            }

            if (entry.target.id === 'bottom-indicator') {
              if (entry.isIntersecting) {
                setFeatureScroll((origin) => ({
                  ...origin,
                  showBottom: false,
                }));
              } else {
                setFeatureScroll((origin) => ({
                  ...origin,
                  showBottom: true,
                }));
              }
            }
          }
        },
        {
          root: featureListRef.current,
        },
      );
    }

    if (commonFeatureListRef.current) {
      commonFeatureObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.target.id === 'common-feature-top-indicator') {
              if (entry.isIntersecting) {
                setCommonFeatureScroll((origin) => ({
                  ...origin,
                  showTop: false,
                }));
              } else {
                setCommonFeatureScroll((origin) => ({
                  ...origin,
                  showTop: true,
                }));
              }
            }

            if (entry.target.id === 'common-feature-bottom-indicator') {
              if (entry.isIntersecting) {
                setCommonFeatureScroll((origin) => ({
                  ...origin,
                  showBottom: false,
                }));
              } else {
                setCommonFeatureScroll((origin) => ({
                  ...origin,
                  showBottom: true,
                }));
              }
            }
          }
        },
        {
          root: commonFeatureListRef.current,
        },
      );
    }

    if (featureObserver && topIndicatorRef.current) {
      featureObserver.observe(topIndicatorRef.current);
    }
    if (featureObserver && bottomIndicatorRef.current) {
      featureObserver.observe(bottomIndicatorRef.current);
    }

    if (commonFeatureObserver && commonFeatureTopIndicatorRef.current) {
      commonFeatureObserver.observe(commonFeatureTopIndicatorRef.current);
    }
    if (commonFeatureObserver && commonFeatureBottomIndicatorRef.current) {
      commonFeatureObserver.observe(commonFeatureBottomIndicatorRef.current);
    }

    return () => {
      if (featureObserver) {
        featureObserver.disconnect();
      }
      if (commonFeatureObserver) {
        commonFeatureObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (pubSub) {
      const handler = async (data) => {
        const alert = await alertHandler(
          projectId,
          data.type ?? data.resourceType,
          data,
        );
        setWebsocketAlert(alert);
      };

      const attendanceReportHandler = (
        data: WebSocketProjectAttendanceReportCreated,
      ) => {
        if (
          (data.projectId === projectId && projectRole === 'owner') ||
          projectRole === 'admin'
        ) {
          enqueueSnackbar(`${t('report-generated')}`, {
            key: `report-created-${data.projectId}-${data.link.id}`,
            variant: 'success',
            autoHideDuration: 30000,
            action: (
              <Icon
                onClick={() =>
                  closeSnackbar(
                    `report-created-${data.projectId}-${data.link.id}`,
                  )
                }
              >
                <CloseSvgIcon width={40} height={40} />
              </Icon>
            ),
          });
        }
      };

      const handleProtocolAlert = async (data: DasloopAlert) => {
        if (data.alert.type === AlertType.REGULAR_SIGNAL_MISSING && projectId) {
          const alert = await alertHandler(projectId, 'dasloop', {
            id: data.id,
            projectId: projectId,
            timestamp: data.timestamp,
          });
          setWebsocketAlert(alert);
        }
      };

      pubSub.subscribe('alert', handler);
      pubSub.subscribe('dasloop_protocol_alert', handleProtocolAlert);

      pubSub.subscribe(
        'project-attendance-report-created',
        attendanceReportHandler,
      );
      pubSub.subscribe(
        'project-plant-attendance-report-created',
        attendanceReportHandler,
      );

      return () => {
        pubSub.unsubscribe('alert', handler);
        pubSub.unsubscribe(
          'project-attendance-report-created',
          attendanceReportHandler,
        );
        pubSub.unsubscribe(
          'project-plant-attendance-report-created',
          attendanceReportHandler,
        );
        pubSub.unsubscribe('dasloop_protocol_alert', handleProtocolAlert);
      };
    }
  }, [pubSub, projectId, projectRole]);

  const data = useQueries({
    queries: [
      {
        queryKey: ['project-role', selectedProjectId],
        queryFn: () =>
          getProjectRole(selectedProjectId as string).then(
            (res) => res.data.data,
          ),
        enabled: !!selectedProjectId,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['project-policies', selectedProjectId],
        queryFn: () =>
          getMyProjectPolicies(selectedProjectId as string).then(
            (res) => res.data.data,
          ),
        enabled: !!selectedProjectId,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['project-contacts', selectedProjectId],
        queryFn: () =>
          getProjectContact(selectedProjectId as string).then(
            (res) => res.data.data,
          ),
        enabled: !!selectedProjectId,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['project-floor-plan', selectedProjectId],
        queryFn: () =>
          getFloorPlans(selectedProjectId as string).then(
            (res) => res.data.data,
          ),
        enabled: !!selectedProjectId,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['get-project-members', projectId],
        queryFn: async () => {
          const res = await getProjectUsers(projectId as string);
          const projectMemberMap = res.data.data.reduce((prev, curr) => {
            prev[curr.id] = curr;
            return prev;
          }, {});
          return projectMemberMap as { [id: string]: User | undefined };
        },
        enabled: !!projectId,
        refetchOnWindowFocus: false,
      },
    ],
  });

  // Extract individual query results
  const [
    projectRoleResult,
    projectPoliciesResult,
    projectContactsResult,
    projectFloorPlanResult,
    projectMembersResult,
  ] = data;

  // Handle side effects in `useEffect`
  useEffect(() => {
    if (projectRoleResult.isSuccess) {
      dispatch(setProjectRole(projectRoleResult.data));
    }
  }, [projectRoleResult.isSuccess, projectRoleResult.data, dispatch]);

  useEffect(() => {
    if (projectPoliciesResult.isSuccess) {
      dispatch(setProjectPolicies(projectPoliciesResult.data));
    }
  }, [projectPoliciesResult.isSuccess, projectPoliciesResult.data, dispatch]);

  useEffect(() => {
    if (projectContactsResult.isSuccess) {
      dispatch(setProjectContact(projectContactsResult.data));
      dispatch(setIsLoadingProjectContact(false));
    } else if (projectContactsResult.isError) {
      dispatch(setProjectContact([]));
      dispatch(setIsLoadingProjectContact(false));
    }
  }, [
    projectContactsResult.isSuccess,
    projectContactsResult.isError,
    projectContactsResult.data,
    dispatch,
  ]);

  useEffect(() => {
    if (projectFloorPlanResult.isSuccess) {
      dispatch(setProjectFloorPlan(projectFloorPlanResult.data));
    }
  }, [projectFloorPlanResult.isSuccess, projectFloorPlanResult.data, dispatch]);

  useEffect(() => {
    if (projectMembersResult.isSuccess) {
      dispatch(setCurrentMembers(projectMembersResult.data));
    }
  }, [projectMembersResult.isSuccess, projectMembersResult.data, dispatch]);

  const { data: dataFeatures, isSuccess: isSuccessFeatureList } = useQuery({
    queryKey: ['get-project-feature-list', selectedProjectId],
    queryFn: async () =>
      getProjectFeatureList(selectedProjectId as string).then(
        (res) => res.data.data,
      ),
    enabled: !!selectedProjectId,
    refetchOnWindowFocus: false,
  });

  // Handle side effect of setting the feature list map in the Redux store
  useEffect(() => {
    if (isSuccessFeatureList && dataFeatures) {
      dispatch(setFeatureListMap(dataFeatures));
    }
  }, [isSuccessFeatureList, dataFeatures]);

  const handleReset = () => {
    dispatch(setProjectRole(null));
    dispatch(setProjectPolicies([]));
    dispatch(setCurrenProject(null));
    dispatch(setCurrentProjectId(null));
    dispatch(setSelectedProjectId(null));
  };

  const policyMap = useMemo(() => {
    const map = new Map<PolicyType, boolean>();
    data[1].data?.forEach((policy) => {
      map.set(policy.name, true);
    });
    return map;
  }, [data?.[1]?.data]);

  const alertReadable =
    data[0].data === 'admin' ||
    data[0].data === 'owner' ||
    policyMap.has('ReadAlert');

  const areaReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadArea');

  const areaWriteable =
    data[0].data === 'admin' ||
    data[0].data === 'owner' ||
    policyMap.has('WriteArea');

  const overviewReadable =
    data[0].data === 'admin' ||
    data[0].data === 'owner' ||
    policyMap.has('ReadProjectOverview');

  const reportReadable =
    data[0].data === 'admin' ||
    data[0].data === 'owner' ||
    policyMap.has('ReadArea') ||
    policyMap.has('WriteArea');

  let disablePa = true;
  if (dataFeatures?.dasloop) {
    disablePa = dataFeatures?.dasloop.metadata.disablePa;
  } else if (dataFeatures?.four_s) {
    disablePa = dataFeatures?.four_s.metadata.disablePa;
  }

  const PAreadable =
    !disablePa && (data[0].data === 'admin' || data[0].data === 'owner');

  const reportDownloadable =
    dataFeatures?.report || data[0].data === 'supervisor';

  const { data: projectAreas, isSuccess } = useQuery({
    queryKey: ['project-area', selectedProjectId],
    queryFn: async () => {
      const areas: Area[] = [];
      const load = async (nextCursor?: string) => {
        const res = await getAreasOfProject(selectedProjectId as string, {
          nextCursor,
          limit: 200,
          selectVisible: false,
        });
        res.data.data.forEach((area) => {
          areas.push(area);
        });

        if (res.data.paging.nextCursor) {
          await load(res.data.paging.nextCursor);
        }
      };

      await load();

      return areas;
    },
    enabled: !!selectedProjectId && areaReadable,
    refetchOnWindowFocus: false,
  });

  // Dispatch the areas data to the Redux store on success
  useEffect(() => {
    if (isSuccess && projectAreas) {
      dispatch(setProjectArea(projectAreas));
    }
  }, [isSuccess, projectAreas, dispatch]);
  const overviewType = currentProjectSetting?.overviewType;

  return (
    <Sidebar className="sidebar" expand={expand}>
      {/^\/dashboard.*/.test(location.pathname) && (
        <div
          className="backward-button"
          onClick={() => {
            handleReset();
            navigate('/project');
          }}
        >
          <div>
            <ChevronLeft />
          </div>
        </div>
      )}
      <NavLink
        className="home"
        to={`/dashboard/${selectedProjectId}`}
        data-cy="nav-sidebar-home"
      >
        <div>
          <HomeSvgIcon sx={{ width: '32px', height: '32px' }} />
        </div>
        {expand && <span> Home</span>}
      </NavLink>
      <Divider expand={expand} sx={{}} />
      {featureScroll.showTop && (
        <IconContainer
          onClick={() => {
            if (featureListRef.current) {
              featureListRef.current.scrollTo(0, 0);
            }
          }}
          sx={{
            width: '100%',
            height: 'unset',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <KeyboardArrowUp />
        </IconContainer>
      )}
      <ul ref={featureListRef} className="feature-list">
        <div ref={topIndicatorRef} id="top-indicator" className="event-block" />
        {dataFeatures?.four_s ? (
          <SSSSMenu
            dataFeatures={dataFeatures}
            selectedProjectId={selectedProjectId}
            enterDelay={ENTER_DELAY}
            expand={expand}
          />
        ) : (
          <BasicMenu
            dataFeatures={dataFeatures}
            selectedProjectId={selectedProjectId}
            enterDelay={ENTER_DELAY}
            expand={expand}
          />
        )}
        <div
          ref={bottomIndicatorRef}
          id="bottom-indicator"
          className="event-block"
        />
      </ul>
      {featureScroll.showBottom && (
        <IconContainer
          onClick={() => {
            if (featureListRef.current) {
              featureListRef.current.scrollTo(
                0,
                featureListRef.current.scrollHeight,
              );
            }
          }}
          sx={{
            width: '100%',
            height: 'unset',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <KeyboardArrowDown />
        </IconContainer>
      )}
      <ExpandButton>
        <Divider
          sx={{
            display: 'flex',
            flex: '1',
            alignItems: 'center',
            width: 'auto',
            marginRight: `${expand ? '16px' : '4px'}`,
          }}
          expand={expand}
        />

        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="40"
          viewBox="0 0 16 40"
          fill="none"
          onClick={() => {
            setExpand((prev) => !prev);
          }}
          style={{
            cursor: 'pointer',
          }}
        >
          <path
            d="M16 40L8 40C3.58172 40 3.50157e-06 36.4183 3.11532e-06 32L1.01717e-06 8C6.3091e-07 3.58172 3.58172 1.08564e-06 8 6.99382e-07L16 0L16 40Z"
            fill="white"
            fillOpacity="0.3"
          />
          <g
            style={{
              transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
              transformOrigin: 'center',
            }}
          >
            <path
              d="M11 20L6.85355 15.8536C6.53857 15.5386 6 15.7617 6 16.2071L6 23.7929C6 24.2383 6.53857 24.4614 6.85355 24.1464L11 20Z"
              fill="white"
            />
          </g>
        </svg>
      </ExpandButton>
      {commonFeatureScroll.showTop && (
        <IconContainer
          onClick={() => {
            if (commonFeatureListRef.current) {
              commonFeatureListRef.current.scrollTo(0, 0);
            }
          }}
          sx={{
            width: '100%',
            height: 'unset',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <KeyboardArrowUp />
        </IconContainer>
      )}
      <ul ref={commonFeatureListRef} className="feature-list">
        <div
          ref={commonFeatureTopIndicatorRef}
          id="common-feature-top-indicator"
          className="event-block"
        />
        {overviewReadable && (
          <NavLink
            to={`/${
              overviewType === 'interactive'
                ? 'overview-cross-filter-dashboard'
                : 'overview-dashboard'
            }/${selectedProjectId}`}
            data-cy="nav-sidebar-overview"
          >
            <DarkTooltip
              title={t('sidebarJson.tooltip.project-overview')}
              placement="right"
              enterDelay={ENTER_DELAY}
            >
              <div>
                <IconContainer className="icon">
                  <img src={overviewSvg} alt="" />
                </IconContainer>
              </div>
            </DarkTooltip>
            {expand && <span>{t('sidebarJson.tooltip.project-overview')}</span>}
          </NavLink>
        )}

        {reportReadable && (
          <NavLink
            to={`/report-dashboard/${selectedProjectId}`}
            data-cy="nav-sidebar-report"
          >
            <DarkTooltip
              title={t('sidebarJson.tooltip.report')}
              placement="right"
              enterDelay={ENTER_DELAY}
            >
              <div>
                <IconContainer className="icon">
                  <img src={reportSvg} alt="" />
                </IconContainer>
              </div>
            </DarkTooltip>
            {expand && <span>{t('sidebarJson.tooltip.report')}</span>}
          </NavLink>
        )}
        {alertReadable && (
          <NavLink
            to={`/alert-dashboard/${selectedProjectId}`}
            data-cy="nav-sidebar-alert"
          >
            <DarkTooltip
              title={t('sidebarJson.tooltip.alert')}
              placement="right"
              enterDelay={ENTER_DELAY}
            >
              <div>
                <IconContainer className="icon">
                  <img src={alertDashboard} alt="" />
                </IconContainer>
              </div>
            </DarkTooltip>
            {expand && <span>{t('sidebarJson.tooltip.alert')}</span>}
          </NavLink>
        )}
        {PAreadable && (
          <NavLink
            to={`public-announcement/${selectedProjectId}`}
            data-cy="nav-sidebar-public-announcement"
          >
            <DarkTooltip
              title={t('sidebarJson.tooltip.public-announcement')}
              placement="right"
              enterDelay={ENTER_DELAY}
            >
              <div>
                <IconContainer className="icon">
                  <img src={paSvg} alt="" />
                </IconContainer>
              </div>
            </DarkTooltip>
            {expand && (
              <span>{t('sidebarJson.tooltip.public-announcement')}</span>
            )}
          </NavLink>
        )}
        {areaWriteable && (
          <NavLink
            to={`/area-management-dashboard/${selectedProjectId}`}
            data-cy="nav-sidebar-area-management"
          >
            <DarkTooltip
              title={t('sidebarJson.tooltip.area')}
              placement="right"
              enterDelay={ENTER_DELAY}
            >
              <div>
                <IconContainer className="icon">
                  <img src={areaManagementSvg} alt="" />
                </IconContainer>
              </div>
            </DarkTooltip>
            {expand && <span>{t('sidebarJson.tooltip.area')}</span>}
          </NavLink>
        )}
        {reportDownloadable && (
          <NavLink
            to={`/data-download-dashboard/${selectedProjectId}`}
            data-cy="nav-sidebar-data-download"
          >
            <DarkTooltip
              title={t('sidebarJson.tooltip.data-download')}
              placement="right"
              enterDelay={ENTER_DELAY}
            >
              <div>
                <IconContainer className="icon">
                  <img src={downloadSvg} alt="" />
                </IconContainer>
              </div>
            </DarkTooltip>
            {expand && <span>{t('sidebarJson.tooltip.data-download')}</span>}
          </NavLink>
        )}
        <div
          ref={commonFeatureBottomIndicatorRef}
          id="common-feature-bottom-indicator"
          className="event-block"
        />
      </ul>
      {commonFeatureScroll.showBottom && (
        <IconContainer
          onClick={() => {
            if (commonFeatureListRef.current) {
              commonFeatureListRef.current.scrollTo(
                0,
                commonFeatureListRef.current.scrollHeight,
              );
            }
          }}
          sx={{
            width: '100%',
            height: 'unset',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <KeyboardArrowDown />
        </IconContainer>
      )}
      <ul className="bottom-list">
        <NavLink to={`/log/${selectedProjectId}`} data-cy="nav-sidebar-log">
          <DarkTooltip
            title={t('sidebarJson.tooltip.log')}
            placement="right"
            enterDelay={ENTER_DELAY}
          >
            <div>
              <IconContainer className="icon">
                <img src={logSvg} alt="" />
              </IconContainer>
            </div>
          </DarkTooltip>
          {expand && <span>{t('sidebarJson.tooltip.log')}</span>}
        </NavLink>
        <NavLink
          to={`/project-setting/${selectedProjectId}`}
          data-cy="nav-sidebar-project-setting"
          rel="noopener noreferrer"
        >
          <DarkTooltip
            title={t('sidebarJson.tooltip.project-setting')}
            placement="right"
            enterDelay={ENTER_DELAY}
          >
            <div>
              <IconContainer className="icon">
                <img src={gearSvg} alt="" />
              </IconContainer>
            </div>
          </DarkTooltip>
          {expand && <span>{t('sidebarJson.tooltip.project-setting')}</span>}
        </NavLink>
      </ul>
    </Sidebar>
  );
};

export default ProjectSidebar;
